import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CallHistoryLogTable from "shared/CallHistoryLog/Table";
import timelineActions from "actions/timeline";
import { useParams, useSearchParams } from "react-router-dom";
import {
    KEY_DATE,
    KEY_PHONE_NUMBER,
    KEY_USER,
    KEY_DURATION,
    KEY_ACTIONS,
    KEY_DIRECTION,
    KEY_SYSTEM_RESULT,
    KEY_NOTE,
    KEY_USER_RESULT,
    KEY_DESTINATION,
    KEY_CONTACT_ACCOUNT,
} from "shared/constants/CALL_HISTORY_LOGS_COLUMNS";
import callHistoryActions from "actions/call_history";
import useModal from "utils/useModal";
import { Modal, Notification, STATUS_TYPES } from "dyl-components";
import { useForm } from "react-hook-form";
import EditCall from "pages/CallHistoryLog/EditCall";
import SendEmailModal from "shared/modals/SendEmailModal";
import AddTaskModal from "shared/modals/AddTaskModal";
import AddEventModal from "shared/modals/AddEventModal";
import useEventForm from "utils/useEventForm";
import useTaskForm from "utils/useTaskForm";
import eventAttendeeActions from "actions/event_attendee";
import uploadActions from "actions/upload";
import { FILE_CATEGORIES } from "utils/FileUtils";
import eventAttachmentActions from "actions/event_attachment";
import groupsActions from "actions/groups";
import eventActions from "actions/event";
import Utils from "shared/EventForm/Utils";
import selfReportActions from "actions/self_report";
import { PhoneUtil, RecordUtils } from "utils";
import AddContactModal from "shared/modals/AddContactModal";
import tag_categories from "actions/tag_categories";
import relationshipsActions from 'actions/relationships';
import { Icon, Loader } from "semantic-ui-react";

const CallLogWrapper = ({ root }) => {
    const timelineApi =
        root === "account"
            ? timelineActions.accountTimelineTab
            : timelineActions.contactTimelineTab;
    const dispatch = useDispatch();
    const [params, setSearchParams] = useSearchParams();
    const { contact_id, account_id } = useParams();
    const record_id = contact_id || account_id;
    const {
        count,
        isLoading,
        data,
        task_labels,
        organizer,
        organizer_email,
        organizer_id,
        current_user,
        accountContacts,
        contactsInHotlistOfficeView,
        accountTimelineContacts,
        isReadingAccountTimelineContacts,
        account,
        listOfTagCategories,
        relationships
    } = useSelector((state) => {
        const {
            first_name = "",
            last_name = "",
            email: organizer_email,
        } = state?.users?.userProfile;
        return {
            count: state?.timeline?.count,
            isLoading: state?.timeline?.isReadingTimeline,
            data: contact_id ? state?.timeline?.timeline.map(d => {
                d.record = {
                    contact_id: Number(contact_id),
                    email: state.contact?.email?.[0]?.email,
                    phone: state.contact?.phone?.[0]?.phone,
                    name: state.contact?.contact?.first_name + " " + state.contact?.contact?.last_name,
                    contact_type: state.contact?.contact.contact_type,
                    job_title: state.contact?.contact.job_title,
                    pipeline_category: state.contact?.current_stage.account_stage
                };
                return d
            }) : state?.timeline?.timeline,
            accountTimelineContacts: state?.timeline?.accountTimelineContacts,
            isReadingAccountTimelineContacts:
                state?.timeline?.isReadingAccountTimelineContacts,
            account: state.account.account,
            task_labels: state?.task?.task_type_labels?.map?.(
                ({ name, id }) => ({
                    key: id,
                    value: id,
                    text: name,
                })
            ),
            organizer_id: state?.auth?.user_id,
            organizer: `${first_name} ${last_name}`,
            current_user: state?.auth,
            accountContacts: state?.account?.contactIds?.map?.(
                ({ first_name, last_name, email, suffix, id }) => ({
                    text: `${first_name || ""} ${last_name || ""}${
                        suffix ? `, ${suffix}` : ""
                    }`,
                    email,
                    key: id,
                    value: id,
                })
            ),
            contactsInHotlistOfficeView:
                state?.groups?.contactsInHotlistOfficeView,
            listOfTagCategories: state?.tag_categories?.listOfTagCategories,
            organizer_email,
            relationships: state.relationships.relationships.map(RecordUtils.getRelationOption)
        };
    });

    const contactAccountOptions = useMemo(() => {
        const groupedContact = [
            {
                name: "Account",
                options: [],
            },
            {
                name: "Linked",
                options: [],
            },
            {
                name: "Unlinked",
                options: [],
            },
        ];
        if (groupedContact[0].options.length === 0 && account?.id) {
            groupedContact[0].options.push({
                key: account.id,
                value: account.id,
                text: account.name,
            });
        }
        accountTimelineContacts?.forEach?.((contact) => {
            if (contact?.linked) {
                groupedContact[1].options.push({
                    key: contact?.contact_id,
                    value: contact?.contact_id,
                    text: `${contact?.first_name} ${contact?.last_name}`,
                });
            } else {
                groupedContact[2].options.push({
                    key: contact?.contact_id,
                    value: contact?.contact_id,
                    text: `${contact?.first_name} ${contact?.last_name}`,
                });
            }
        });
        if (groupedContact[1].options.length) {
            groupedContact[1].options.push({
                key: "All",
                value: "All",
                text: "All",
                all: true,
            });
        }
        return groupedContact;
    }, [accountTimelineContacts, account]);

    const columns = [
        KEY_DATE,
        KEY_PHONE_NUMBER,
        KEY_USER,
        KEY_DIRECTION,
        KEY_SYSTEM_RESULT,
        KEY_USER_RESULT,
        KEY_DURATION,
        KEY_NOTE,
        KEY_ACTIONS,
        KEY_DESTINATION,
        ...(root === "account" ? [KEY_CONTACT_ACCOUNT] : []),
    ];

    const isColumnHidden = (column) => {
        return !columns.includes(column);
    };

    const editLogModal = useModal(),
        emailModal = useModal(),
        taskModal = useModal(),
        eventModal = useModal(),
        addContactModal = useModal();

    const {
        control,
        reset: resultsReset,
        handleSubmit,
        formState: { isValid: resultsIsValid, isDirty: resultsIsDirty },
    } = useForm({
        mode: "onChange",
        defaultValues: {
            call_result: editLogModal?.data?.user_result ?? "",
            call_notes: editLogModal?.data?.note ?? "",
            call_tag: null,
            related_to: null,
            sub_tag: null,
        },
        reValidateMode: "all",
    });

    const {
        control: eventControl,
        watch: eventWatch,
        isValid: eventIsValid,
        isDirty,
        trigger: eventTrigger,
        setValue: eventSetValue,
        getValues,
        reset,
        isAllowedToModify,
        eventBeingEdited,
        loadEvent,
        handleSubmit: handleSubmitEvent,
        resetField,
    } = useEventForm({ contact_id: editLogModal?.data?.record?.contact_id });

    const {
        control: taskControl,
        watch: taskWatch,
        isValid: taskIsValid,
        isDirty: taskIsDirty,
        trigger: taskTrigger,
        setValue: taskSetValue,
        getValues: taskGetValues,
        clearErrors: taskClearErrors,
        setError: taskSetError,
        reset: taskReset,
        taskBeingEdited,
        loadTask,
        addTask,
        state,
        setState,
        onDelete: onDeleteTask,
        onUpdate: onUpdateTask,
        handleSubmit: handleSubmitTask,
        isAllowedToModify: isTaskAllowedToModify,
    } = useTaskForm({
        isContact: false,
        isModal: true,
        open: taskModal?.isVisible,
    });

    const onFilter = ({ name, value }) => {
        const updatedParams = new URLSearchParams(params);
        const hasValue =
            typeof value === "string" ? value.trim() : value?.length;

        if (hasValue) {
            if (name === "contact_id") {
                // Separate account IDs from contact IDs
                const accountId = account?.id;
                const currentAccountId = params.get("account_id");

                const hasAccountInValue = value?.includes?.(accountId);

                const contactIds =
                    value?.filter?.((id) => id !== accountId) || [];

                if (hasAccountInValue) {
                    if (
                        !currentAccountId ||
                        currentAccountId !== String(accountId)
                    ) {
                        updatedParams.set("account_id", accountId);
                    }
                } else {
                    if (currentAccountId) {
                        updatedParams.delete("account_id");
                    }
                }

                if (contactIds.length > 0) {
                    updatedParams.set("contact_id", contactIds.join(","));
                } else {
                    updatedParams.delete("contact_id");
                }
            } else {
                updatedParams.set(
                    name,
                    Array.isArray(value) ? value.join(",") : value
                );
            }
        } else {
            if (name === "contact_id") {
                updatedParams.delete("contact_id");
                updatedParams.delete("account_id");
            } else {
                updatedParams.delete(name);
            }
        }

        setSearchParams(updatedParams);
    };

    const onSaveResults = async (data) => {
        const { id: uuid, ...entry } = editLogModal?.data;
        const body = {
            notes: data.call_notes || "",
            result: data.call_result || null,
            tag: data?.sub_tag,
        };
        try {
            if (editLogModal?.data?.type === "log_call") {
                dispatch(
                    selfReportActions.updateCallActivity(uuid, {
                        contact_id: parseInt(contact_id),
                        id: uuid,
                        destination: entry?.destination ?? "",
                        direction:
                            entry?.call?.direction ??
                            entry?.direction ??
                            "outbound",
                        occurred: entry?.ts,
                        source: entry?.source ?? "",
                        notes: body.notes,
                        result: body.result,
                        user_id: current_user.user_id,
                        length: entry?.length ?? 0,
                    })
                );
            } else {
                dispatch(callHistoryActions.setUserResult(uuid, body));
            }
            resultsReset(null, { keepValues: true });
            Notification.alert(
                "Successfully updated Call Results!",
                STATUS_TYPES.SUCCESS
            );
            dispatch(timelineApi(record_id, params));
            editLogModal.closeModal();
        } catch (error) {
            console.log(error);
            Notification.alert(
                "Failed to update Call Results",
                STATUS_TYPES.ERROR
            );
        }
    };

    const onReportCall = (call, payload) => {
        if (!call?.call_uuid) return;
        try {
            dispatch(
                callHistoryActions.reportCall(
                    {
                        notes: "",
                        problem: payload?.problem_type,
                    },
                    { call_uuid: call?.call_uuid }
                )
            );
            dispatch(timelineApi(record_id, params));
        } catch (error) {
            console.log(error);
        }
    };

    const onAddEvent = async (event) => {
        const id = await dispatch(
            eventActions.addEvent(Utils.toPayload(event))
        );
        return id;
    };

    const onAddAttendees = (event_id, params) => {
        return dispatch(
            eventAttendeeActions.addAttendees(params, null, event_id)
        );
    };

    const addAttendees = (event_id, usersToAdd) => {
        let params = {
            payload: usersToAdd,
            integration_id: null,
            access_token: null,
        };
        if (usersToAdd.length > 0) {
            return onAddAttendees(event_id, params);
        }
        return Promise.resolve();
    };

    const onUpload = (file, file_type) => {
        return dispatch(uploadActions.uploadFiles(file, file_type));
    };

    const onAddAttachments = (attachments, user_id, event_id) => {
        return dispatch(
            eventAttachmentActions.addAttachments(
                attachments,
                { user_id },
                event_id
            )
        );
    };

    const addAttachments = async (files, event_id) => {
        if (files && files.length > 0) {
            const toUpload = files.filter((file) => file.id === undefined);
            const toCopy = files
                .filter((file) => file.id)
                .map((file) => ({
                    file_id: file.file_id,
                    name: file.name,
                    size: file.size,
                }));
            const uploadedFiles = await onUpload(
                toUpload,
                FILE_CATEGORIES.EVENT
            );
            const newFiles = toUpload.map((_, idx) => {
                return {
                    name: files[idx].name,
                    file_id: uploadedFiles[idx],
                    size: files[idx].size,
                };
            });
            return onAddAttachments(
                [...newFiles, ...toCopy],
                current_user.user_id,
                event_id
            );
        }
        return Promise.resolve();
    };

    const addEvent = async () => {
        const values = getValues();
        const {
            all_day,
            start_date,
            start_time,
            end_date,
            end_time,
            users,
            attachments,
            contacts,
            timezone,
            related_to,
        } = values;

        const attendees = [
            ...contacts.map((contact_id) => ({
                accepted: "no",
                contact_id,
                emailed: false,
            })),
            ...users.map((user_id) => ({
                accepted: "no",
                user_id,
                emailed: false,
            })),
        ];

        // TODO: add recurring event properties
        const payload = {
            complete: false,
            conference_phone: values.phone_number,
            conference_pin: values.pin,
            conference_url: values.conference_line,
            content: values.content,
            content_type: "text/html",
            label: values.label ? values.label : 0,
            location: values.location,
            name: values.name,
            all_day,
            start_date,
            start_time,
            end_date,
            end_time,
            // "recurring_event_id": 0,
            organizer_id,
            organizer,
            organizer_email,
            users: values.users,
            contact_id: values.contacts,
            timezone,
            related_to,
        };

        try {
            const { id } = await onAddEvent(payload);
            await addAttendees(id, attendees);
            await addAttachments(attachments, id, "pdf");
            eventModal?.closeModal();
            Notification.alert(
                "Successfully created event!",
                STATUS_TYPES.SUCCESS
            );
        } catch (e) {
            console.log(e);
            Notification.alert("Failed to create event", STATUS_TYPES.ERROR);
        }
    };

    const onHotlistClick = async () => {
        try {
            if (
                contactsInHotlistOfficeView.includes(
                    editLogModal?.data?.record?.contact_id
                )
            ) {
                await dispatch(
                    groupsActions.removeHotlist(
                        editLogModal?.data?.record?.contact_id
                    )
                );
                await dispatch(
                    groupsActions.checkHotlistOfficeView({
                        contacts: [editLogModal?.data?.record?.contact_id],
                    })
                );
                Notification.alert(
                    "Successfully removed contact from hotlist!",
                    STATUS_TYPES.SUCCESS,
                    true
                );
            } else {
                await dispatch(
                    groupsActions.addToHotlist({
                        contact_id: [editLogModal?.data?.record?.contact_id],
                    })
                );
                await dispatch(
                    groupsActions.checkHotlistOfficeView({
                        contacts: [editLogModal?.data?.record?.contact_id],
                    })
                );
                Notification.alert(
                    "Successfully added contact to hotlist!",
                    STATUS_TYPES.SUCCESS,
                    true
                );
            }
        } catch (error) {
            console.log(error);
            if (
                contactsInHotlistOfficeView.includes(
                    editLogModal?.data?.record?.contact_id
                )
            ) {
                Notification.alert(
                    "Problem removing contact from hotlist!",
                    STATUS_TYPES.ERROR,
                    true
                );
            } else {
                Notification.alert(
                    "Problem adding contact to hotlist!",
                    STATUS_TYPES.ERROR,
                    true
                );
            }
        }
    };

    const onTaskClick = () => {
        loadTask({
            contact: editLogModal?.data?.record?.contact_id,
        });
        taskModal?.openModal?.();
    };

    const onEventClick = () => {
        loadEvent(null);
        eventModal?.openModal();
    };

    const [isLoadingCallLog, setIsLoadingCallLog] = useState(false);

    const onEditLog = (call) => {
        if (!call?.call_uuid) return;
        setIsLoadingCallLog(true);
        if (call?.record?.contact_type === "person") {
            dispatch(relationshipsActions.getContactRelations({ person_id: call?.record?.contact_id }));
        }
        dispatch(
            callHistoryActions.getCallDetails(
                call?.call_uuid,
                call?.direction === "internal" && { user_id: true }
            )
        ).then(logResponse => {
            const callTag = call?.tags?.[0] || "";
            const parent_id = listOfTagCategories?.find?.((category) =>
                category?.tags?.some?.((tag) => tag?.id === callTag?.id)
            )?.id;

            resultsReset({
                call_notes: call?.note,
                call_result: call?.user_result,
                call_tag: parent_id,
                sub_tag: callTag?.id,
                related_to: logResponse?.related_id || null,
            });
            const { account_id, is_customer, pipeline_id, pipeline } = logResponse?.contacts?.[0] || {};
            editLogModal?.openModal({
                ...call,
                ...(is_customer && account_id ? {
                    customer_info: {
                        account_id,
                        pipeline_id,
                        pipeline
                    }
                } : {})
            });
        }).finally(() => {
            setIsLoadingCallLog(false);
        });
    };

    const onAddContact = (call) => {
        addContactModal?.openModal({
            ...call,
            phone: PhoneUtil.sanitizePhoneNumber(call?.phone),
        });
    };

    const onMarkVoicemail = (call_uuid, value, refetch = true) => {
        if (!call_uuid) return;
        try {
            dispatch(
                callHistoryActions.setVoicemailListened(
                    null,
                    { listened: value },
                    { call_uuid }
                )
            );
            Notification.alert(
                `Marked successfully ${value ? "listened" : "unlistened"}`,
                STATUS_TYPES.SUCCESS
            );
            if (refetch) {
                dispatch(timelineApi(record_id, params));
            }
        } catch (error) {
            Notification.alert("Failed to update", STATUS_TYPES.ERROR);
            console.log(error);
        }
    };

    const onPageChange = (_, { activePage }) => {
        const updatedParams = new URLSearchParams(params);
        updatedParams.set("page", activePage);
        setSearchParams(updatedParams);
    };

    const onClickToDial = () => {
        const { contact_id, account_id } = editLogModal?.data?.record || {};
        const { phone: destination } = editLogModal?.data || {}
        editLogModal?.closeModal?.();
        PhoneUtil.C2D({payload: { contact_id, account_id, destination }, dispatch});
    }

    useEffect(() => {
        dispatch(timelineApi(record_id, params));
    }, [record_id, dispatch, params, timelineApi]);

    useEffect(() => {
        if (root !== "account") return;
        dispatch(
            timelineActions.getAccountTimelineContacts(account_id, {
                limit: 200,
            })
        );
    }, [dispatch, account_id, root]);

    useEffect(
        () => dispatch(tag_categories.getListOfTagCategories()),
        [dispatch]
    );

    const isEditingCallLogForContact = editLogModal?.data?.record?.contact_type === "person";

    return (
        <div className="CallLogWrapper">
            <CallHistoryLogTable
                root={root}
                isTimelineTab
                isColumnHidden={isColumnHidden}
                count={count}
                data={data}
                isLoading={isLoading}
                onFilter={onFilter}
                onPageChange={onPageChange}
                onEditLog={onEditLog}
                onReportCall={onReportCall}
                onMarkVoicemail={onMarkVoicemail}
                onAddContact={onAddContact}
                isReadingContacts={isReadingAccountTimelineContacts}
                contactAccountOptions={contactAccountOptions}
            />
            <Modal
                open={isLoadingCallLog || editLogModal?.isVisible}
                onClose={editLogModal?.closeModal}
                className="EditCallResult"
            >
                <Modal.Content scrolling>
                    {!isLoadingCallLog ? (
                        <EditCall
                            editLogModal={editLogModal}
                            control={control}
                            onSave={handleSubmit(onSaveResults)}
                            onSaveDisabled={!resultsIsDirty || !resultsIsValid}
                            onEmailClick={emailModal?.openModal}
                            onTaskClick={onTaskClick}
                            onEventClick={onEventClick}
                            onHotlistClick={onHotlistClick}
                            isInHotlist={contactsInHotlistOfficeView.includes(
                                editLogModal?.data?.record?.contact_id
                            )}
                            isSelfReportCall={
                                editLogModal?.data?.type === "log_call"
                            }
                            onClickToDial={onClickToDial}
                            relatedToOptions={isEditingCallLogForContact ? relationships : (() => {
                                const { account_id, pipeline_id, pipeline } = editLogModal?.data?.customer_info || {};
                                if (account_id && pipeline_id && pipeline) {
                                    return [
                                        {
                                            key: pipeline_id,
                                            value: pipeline_id,
                                            text: pipeline,
                                            content: <span><Icon className='fas fa-crown' /> {pipeline}</span>
                                        }
                                    ]
                                }
                                return [];
                            })()}
                        />
                    ) : <Loader active inline="centered" />}
                </Modal.Content>
            </Modal>
            <Modal
                open={addContactModal?.isVisible}
                onClose={addContactModal?.closeModal}
            >
                <AddContactModal
                    isOfficeView
                    unknownNumber={addContactModal?.data?.phone}
                    onClose={addContactModal?.closeModal}
                    onSuccessCallback={() =>
                        dispatch(callHistoryActions.getCalls(params))
                    }
                />
            </Modal>
            {emailModal?.isVisible && (
                <SendEmailModal
                    open={emailModal?.isVisible}
                    onClose={emailModal?.closeModal}
                    contact_id={editLogModal?.data?.record?.contact_id}
                    account_id={editLogModal?.data?.record?.account_id}
                    isAccountModal={!editLogModal?.data?.record?.contact_id}
                />
            )}
            <AddTaskModal
                open={taskModal?.isVisible}
                onClose={taskModal?.closeModal}
                state={state}
                control={taskControl}
                watch={taskWatch}
                isValid={taskIsValid}
                isDirty={taskIsDirty}
                trigger={taskTrigger}
                setValue={taskSetValue}
                getValues={taskGetValues}
                clearErrors={taskClearErrors}
                setError={taskSetError}
                reset={taskReset}
                taskBeingEdited={taskBeingEdited}
                loadTask={loadTask}
                addTask={addTask}
                setState={setState}
                onDelete={onDeleteTask}
                onUpdate={onUpdateTask}
                task_labels={task_labels}
                organizer_id={organizer_id}
                organizer={organizer}
                organizer_email={organizer_email}
                handleSubmit={handleSubmitTask}
                onRefresh={taskModal?.closeModal}
                email={{ body: "", subject: "" }}
                isAllowedToModify={isTaskAllowedToModify}
                hiddenContactSelector
            />
            <AddEventModal
                open={eventModal?.isVisible}
                onClose={eventModal?.closeModal}
                onSave={handleSubmitEvent(addEvent)}
                selected_users={[]}
                isAllowedToModify={isAllowedToModify}
                control={eventControl}
                watch={eventWatch}
                isValid={eventIsValid}
                isDirty={isDirty}
                trigger={eventTrigger}
                setValue={eventSetValue}
                getValues={getValues}
                reset={reset}
                resetField={resetField}
                eventBeingEdited={eventBeingEdited}
                setNotificationContactName={() => {}}
                accountContacts={accountContacts}
                currentContact={{
                    text: editLogModal?.data?.record?.name || "1",
                    key: editLogModal?.data?.record?.contact_id || "",
                    value: editLogModal?.data?.record?.contact_id || "",
                    email: editLogModal?.data?.record?.email || "",
                }}
            />
        </div>
    );
};

export default CallLogWrapper;
